import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActualServer,
  BudgetCategory,
  BudgetCategoryGroup,
  BudgetMonthData
} from '../types';
import {
  formatBudgetCurrency,
  formatSpentAmount,
  formatMonthLabel,
  centsToDisplay
} from '../utils/budgetFormat';
import { updateCategoryBudget } from '../utils/budgetApi';
import './BudgetPage.css';

interface BudgetViewProps {
  budgetMonth: BudgetMonthData;
  currency?: string;
  selectedMonth: string;
  server: ActualServer;
  onPreviousMonth: () => void;
  onNextMonth: () => void;
  canGoPrevious?: boolean;
  canGoNext?: boolean;
  onRefresh: () => void;
  onBudgetMonthReplace: (data: BudgetMonthData) => void;
  onCategoryBudgetPatch: (categoryId: string, amountCents: number) => void;
  onAllocate?: () => void;
  loading?: boolean;
}

function visibleGroups(groups: BudgetCategoryGroup[]): BudgetCategoryGroup[] {
  return groups
    .filter(group => !group.hidden)
    .map(group => ({
      ...group,
      categories: (group.categories || []).filter(cat => !cat.hidden)
    }))
    .filter(group => group.categories.length > 0 || group.is_income);
}

function displayToCents(value: string): number | null {
  const cleaned = value.replace(/,/g, '').trim();
  if (!cleaned) return 0;
  const parsed = Number(cleaned);
  if (!Number.isFinite(parsed) || parsed < 0) return null;
  return Math.round(parsed * 100);
}

function AmountCell({
  amount,
  currency,
  negativeIsBad = false,
  spent = false
}: {
  amount?: number;
  currency: string;
  negativeIsBad?: boolean;
  spent?: boolean;
}) {
  const value = amount ?? 0;
  const className = negativeIsBad && value < 0 ? 'budget-amount negative' : 'budget-amount';
  const formatted = spent ? formatSpentAmount(value, currency) : formatBudgetCurrency(value, currency);
  return <span className={className}>{formatted}</span>;
}

function BudgetedCell({
  amountCents,
  currency,
  categoryId,
  month,
  server,
  onPatch,
  onReplace,
  onRefresh
}: {
  amountCents: number;
  currency: string;
  categoryId: string;
  month: string;
  server: ActualServer;
  onPatch: (categoryId: string, amountCents: number) => void;
  onReplace: (data: BudgetMonthData) => void;
  onRefresh: () => void;
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const startEditing = () => {
    if (saving) return;
    setInputValue(centsToDisplay(amountCents).toFixed(2));
    setEditing(true);
    setError(false);
    requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    });
  };

  const cancelEditing = () => {
    setEditing(false);
    setError(false);
  };

  const commitEdit = async () => {
    const newCents = displayToCents(inputValue);
    if (newCents === null) {
      setError(true);
      return;
    }

    if (newCents === amountCents) {
      cancelEditing();
      return;
    }

    const previousCents = amountCents;
    setSaving(true);
    setEditing(false);
    onPatch(categoryId, newCents);

    try {
      const updatedMonth = await updateCategoryBudget(server, month, categoryId, newCents);
      onReplace(updatedMonth);
    } catch {
      onPatch(categoryId, previousCents);
      setError(true);
      onRefresh();
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      void commitEdit();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      cancelEditing();
    }
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        type="number"
        className={`budget-amount-input${error ? ' has-error' : ''}`}
        min={0}
        step="0.01"
        value={inputValue}
        onChange={event => {
          setInputValue(event.target.value);
          setError(false);
        }}
        onBlur={() => void commitEdit()}
        onKeyDown={handleKeyDown}
        aria-label={t('editBudgeted.ariaLabel', { ns: 'budget' })}
      />
    );
  }

  return (
    <button
      type="button"
      className={`budget-amount-btn${saving ? ' saving' : ''}${error ? ' has-error' : ''}`}
      onClick={startEditing}
      disabled={saving}
      aria-label={t('editBudgeted.ariaLabel', { ns: 'budget' })}
      title={t('editBudgeted.hint', { ns: 'budget' })}
    >
      <span className="budget-amount">{formatBudgetCurrency(amountCents, currency)}</span>
    </button>
  );
}

function CategoryRow({
  category,
  currency,
  isIncome,
  month,
  server,
  onCategoryBudgetPatch,
  onBudgetMonthReplace,
  onRefresh
}: {
  category: BudgetCategory;
  currency: string;
  isIncome: boolean;
  month: string;
  server: ActualServer;
  onCategoryBudgetPatch: (categoryId: string, amountCents: number) => void;
  onBudgetMonthReplace: (data: BudgetMonthData) => void;
  onRefresh: () => void;
}) {
  const { t } = useTranslation();

  return (
    <div className="budget-row budget-category-row">
      <div className="budget-cell budget-cell-name">{category.name}</div>
      {isIncome ? (
        <>
          <div className="budget-cell budget-cell-amount" />
          <div className="budget-cell budget-cell-amount">
            <AmountCell amount={category.received} currency={currency} />
          </div>
          <div className="budget-cell budget-cell-amount" />
        </>
      ) : (
        <>
          <div className="budget-cell budget-cell-amount">
            <BudgetedCell
              amountCents={category.budgeted ?? 0}
              currency={currency}
              categoryId={category.id}
              month={month}
              server={server}
              onPatch={onCategoryBudgetPatch}
              onReplace={onBudgetMonthReplace}
              onRefresh={onRefresh}
            />
          </div>
          <div className="budget-cell budget-cell-amount">
            <AmountCell amount={category.spent} currency={currency} spent />
          </div>
          <div className="budget-cell budget-cell-amount">
            <AmountCell amount={category.balance} currency={currency} negativeIsBad />
          </div>
        </>
      )}
      <div className="budget-cell budget-cell-label budget-mobile-label">
        {isIncome ? t('columns.received', { ns: 'budget' }) : t('columns.balance', { ns: 'budget' })}
      </div>
    </div>
  );
}

function GroupSection({
  group,
  currency,
  month,
  server,
  onCategoryBudgetPatch,
  onBudgetMonthReplace,
  onRefresh
}: {
  group: BudgetCategoryGroup;
  currency: string;
  month: string;
  server: ActualServer;
  onCategoryBudgetPatch: (categoryId: string, amountCents: number) => void;
  onBudgetMonthReplace: (data: BudgetMonthData) => void;
  onRefresh: () => void;
}) {
  const { t } = useTranslation();
  const isIncome = Boolean(group.is_income);

  return (
    <section className="budget-group">
      <div className={`budget-row budget-group-header ${isIncome ? 'income' : ''}`}>
        <div className="budget-cell budget-cell-name">{group.name}</div>
        {isIncome ? (
          <>
            <div className="budget-cell budget-cell-amount" />
            <div className="budget-cell budget-cell-amount">
              <AmountCell amount={group.received} currency={currency} />
            </div>
            <div className="budget-cell budget-cell-amount" />
          </>
        ) : (
          <>
            <div className="budget-cell budget-cell-amount">
              <AmountCell amount={group.budgeted} currency={currency} />
            </div>
            <div className="budget-cell budget-cell-amount">
              <AmountCell amount={group.spent} currency={currency} spent />
            </div>
            <div className="budget-cell budget-cell-amount">
              <AmountCell amount={group.balance} currency={currency} negativeIsBad />
            </div>
          </>
        )}
      </div>
      {group.categories.map(category => (
        <CategoryRow
          key={category.id}
          category={category}
          currency={currency}
          isIncome={isIncome}
          month={month}
          server={server}
          onCategoryBudgetPatch={onCategoryBudgetPatch}
          onBudgetMonthReplace={onBudgetMonthReplace}
          onRefresh={onRefresh}
        />
      ))}
      {isIncome && group.categories.length === 0 && (
        <div className="budget-row budget-category-row muted">
          <div className="budget-cell budget-cell-name">{t('income', { ns: 'budget' })}</div>
          <div className="budget-cell budget-cell-amount" />
          <div className="budget-cell budget-cell-amount">
            <AmountCell amount={group.received} currency={currency} />
          </div>
          <div className="budget-cell budget-cell-amount" />
        </div>
      )}
    </section>
  );
}

export const BudgetView: React.FC<BudgetViewProps> = ({
  budgetMonth,
  currency = 'USD',
  selectedMonth,
  server,
  onPreviousMonth,
  onNextMonth,
  canGoPrevious = true,
  canGoNext = true,
  onRefresh,
  onBudgetMonthReplace,
  onCategoryBudgetPatch,
  onAllocate,
  loading = false
}) => {
  const { t } = useTranslation();
  const groups = useMemo(
    () => visibleGroups(budgetMonth.categoryGroups || []),
    [budgetMonth.categoryGroups]
  );
  const hasUnallocatedFunds = (budgetMonth.toBudget ?? 0) > 0;

  return (
    <div className="budget-view">
      <div className="budget-toolbar">
        <div className="budget-month-nav">
          <button
            type="button"
            className="budget-nav-btn"
            onClick={onPreviousMonth}
            disabled={!canGoPrevious || loading}
            aria-label={t('monthNavigation.previous', { ns: 'budget' })}
          >
            ‹
          </button>
          <h2 className="budget-month-title">{formatMonthLabel(selectedMonth)}</h2>
          <button
            type="button"
            className="budget-nav-btn"
            onClick={onNextMonth}
            disabled={!canGoNext || loading}
            aria-label={t('monthNavigation.next', { ns: 'budget' })}
          >
            ›
          </button>
        </div>
        <button
          type="button"
          className="budget-refresh-btn"
          onClick={onRefresh}
          disabled={loading}
        >
          {t('actions.refresh', { ns: 'budget' })}
        </button>
      </div>

      {hasUnallocatedFunds && onAllocate && (
        <div className="budget-allocation-banner">
          <p>{t('allocation.banner', {
            ns: 'budget',
            amount: formatBudgetCurrency(budgetMonth.toBudget, currency)
          })}</p>
          <button type="button" className="budget-allocation-btn" onClick={onAllocate}>
            {t('allocation.allocateButton', { ns: 'budget' })}
          </button>
        </div>
      )}

      <div className="budget-summary">
        <div className="budget-summary-item highlight">
          <span className="budget-summary-label">{t('summary.toBudget', { ns: 'budget' })}</span>
          <span className="budget-summary-value">
            {formatBudgetCurrency(budgetMonth.toBudget, currency)}
          </span>
        </div>
        <div className="budget-summary-item">
          <span className="budget-summary-label">{t('summary.income', { ns: 'budget' })}</span>
          <span className="budget-summary-value">
            {formatBudgetCurrency(budgetMonth.totalIncome, currency)}
          </span>
        </div>
        <div className="budget-summary-item">
          <span className="budget-summary-label">{t('summary.budgeted', { ns: 'budget' })}</span>
          <span className="budget-summary-value">
            {formatBudgetCurrency(budgetMonth.totalBudgeted, currency)}
          </span>
        </div>
        <div className="budget-summary-item">
          <span className="budget-summary-label">{t('summary.spent', { ns: 'budget' })}</span>
          <span className="budget-summary-value">
            {formatSpentAmount(budgetMonth.totalSpent, currency)}
          </span>
        </div>
        <div className="budget-summary-item">
          <span className="budget-summary-label">{t('summary.balance', { ns: 'budget' })}</span>
          <span className="budget-summary-value">
            {formatBudgetCurrency(budgetMonth.totalBalance, currency)}
          </span>
        </div>
      </div>

      <div className="budget-table">
        <div className="budget-row budget-table-header">
          <div className="budget-cell budget-cell-name">{t('columns.category', { ns: 'budget' })}</div>
          <div className="budget-cell budget-cell-amount">{t('columns.budgeted', { ns: 'budget' })}</div>
          <div className="budget-cell budget-cell-amount">{t('columns.spent', { ns: 'budget' })}</div>
          <div className="budget-cell budget-cell-amount">{t('columns.balance', { ns: 'budget' })}</div>
        </div>

        {groups.length === 0 ? (
          <div className="budget-empty-inline">{t('empty.noCategories', { ns: 'budget' })}</div>
        ) : (
          groups.map(group => (
            <GroupSection
              key={group.id}
              group={group}
              currency={currency}
              month={selectedMonth}
              server={server}
              onCategoryBudgetPatch={onCategoryBudgetPatch}
              onBudgetMonthReplace={onBudgetMonthReplace}
              onRefresh={onRefresh}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default BudgetView;
